"use client";

import React, { useState, useEffect } from 'react';
import Image from "next/image";
import { useRouter } from "next/navigation";
import { formatarData } from "@/lib/utils";
import CardNewsMedium from '@/components/molecules/CardNewsMedium';
import { Modal } from '@/components/atoms/modal';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/atoms/carousel";

interface NoticiaDetalheClientProps {
  noticiaData: {
    documentId?: string;
    title?: string;
    description?: string;
    publishedAt?: string;
    Tag?: string;
    image?: {
      url?: string;
      alternativeText?: string;
    };
    cms_tema_noticia?: {
      label?: string;
    };
    galeria?: {
      url?: string;
      alternativeText?: string;
    }[];
    slug?: string;
  } | null;
  error: string | null;
  outrasNoticias?: any[];
  titleGaleria?: string;
}

const NoticiaDetalheClient: React.FC<NoticiaDetalheClientProps> = ({
  noticiaData,
  error,
  outrasNoticias = [],
  titleGaleria,
}) => {
  const router = useRouter();
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageLoadingStates, setImageLoadingStates] = useState<{ [key: number]: boolean }>({});

  // Preparar dados da galeria para uso
  const galeriaImages = Array.isArray(noticiaData?.galeria)
    ? noticiaData.galeria
      .filter((img): img is { url: string; alternativeText?: string } =>
        typeof img?.url === 'string' && !!img.url
      )
      .map(img => ({
        url: img.url,
        alt: img.alternativeText || 'Imagem da galeria'
      }))
    : [];

  const openGalleryModal = (index: number = 0) => {
    setSelectedImageIndex(index);
    setIsGalleryModalOpen(true);
  };

  const closeGalleryModal = () => {
    setIsGalleryModalOpen(false);
    setSelectedImageIndex(0);
  };

  const handleImageLoad = (index: number) => {
    setImageLoadingStates(prev => ({ ...prev, [index]: false }));
  };



  // Navegação por teclado no modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isGalleryModalOpen) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          setSelectedImageIndex(prev =>
            prev > 0 ? prev - 1 : galeriaImages.length - 1
          );
          break;
        case 'ArrowRight':
          event.preventDefault();
          setSelectedImageIndex(prev =>
            prev < galeriaImages.length - 1 ? prev + 1 : 0
          );
          break;
        case 'Escape':
          event.preventDefault();
          closeGalleryModal();
          break;
      }
    };

    if (isGalleryModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isGalleryModalOpen, galeriaImages.length]);

  if (!noticiaData) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-400 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Carregando detalhes da notícia...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-xl mx-auto">
          <h3 className="text-red-700 text-xl font-semibold mb-2">Erro ao carregar notícia</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <p className="text-gray-600 mb-6">A notícia solicitada não foi encontrada ou ocorreu um erro ao carregar seus detalhes.</p>
          <button
            onClick={() => router.push('/noticias-eventos')}
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
          >
            Ver todas as notícias
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-24 ">
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 lg:gap-4">
        {/* Meta, categoria e título */}
        <div className="xl:col-span-8 xl:offset-0">
          <div className="flex flex-col gap-3 md:gap-4 lg:mt-10">
            {/* Título */}
            <h1 className="text-[#2A2A2A] text-2xl md:text-3xl lg:text-4xl font-medium font-exo-2 leading-tight mb-8">
              {noticiaData.title}
            </h1>

            {/* Data de publicação */}
            <div className="flex items-center gap-8 text-zinc-500">
             
              <span className="text-sm sm:text-base font-light flex items-center gap-2">
                 <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                <g clipPath="url(#clip0_2054_69710)">
                  <path d="M5.6 0V1.6H10.4V0H12V1.6H15.2C15.6418 1.6 16 1.95818 16 2.4V15.2C16 15.6418 15.6418 16 15.2 16H0.8C0.358176 16 0 15.6418 0 15.2V2.4C0 1.95818 0.358176 1.6 0.8 1.6H4V0H5.6ZM14.4 8H1.6V14.4H14.4V8ZM4.8 9.6V11.2H3.2V9.6H4.8ZM8.8 9.6V11.2H7.2V9.6H8.8ZM12.8 9.6V11.2H11.2V9.6H12.8ZM4 3.2H1.6V6.4H14.4V3.2H12V4.8H10.4V3.2H5.6V4.8H4V3.2Z" fill="#377396"/>
                </g>
                <defs>
                  <clipPath id="clip0_2054_69710">
                    <rect width="16" height="16" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
                {noticiaData.publishedAt ? formatarData(noticiaData.publishedAt) : "Data indisponível"}
              </span>
               {/* Categoria */}
            {noticiaData.Tag && (
              <span className="inline-block text-[#598648] text-sm sm:text-base font-medium uppercase">
                {noticiaData.Tag}
              </span>
            )}
            </div>

           

          </div>
        </div>
        {/* Conteúdo principal */}
        <div className="xl:col-span-8 order-2">

          <div className='pt-0'>
            {/* Topo com imagem à esquerda e meta/título à direita */}
            <div className="grid grid-cols-1">
              {/* Imagem principal */}
              <div>
                {(() => {
                  const mainImgUrl = (noticiaData as any)?.image?.url || (noticiaData as any)?.Image?.url
                  const mainImgAlt = (noticiaData as any)?.image?.alternativeText || (noticiaData as any)?.Image?.alternativeText || noticiaData.title || "Imagem da notícia"
                  return mainImgUrl ? (
                    <div className="relative w-full min-h-[482px]  rounded-xl overflow-hidden">
                      <Image
                        src={mainImgUrl}
                        alt={mainImgAlt}
                        fill
                        className="object-cover rounded-3xl object-center-bottom"
                        priority
                      />
                    </div>
                  ) : null
                })()}
              </div>


            </div>

            {/* Descrição abaixo do bloco superior */}
            <div
              className="prose prose-sm sm:prose-base lg:prose-lg max-w-none text-justify text-[#92949F] font-light font-poppins leading-relaxed sm:leading-loose mt-6"
              dangerouslySetInnerHTML={{ __html: noticiaData.description || "" }}
            />
          </div>
        </div>

        {/* Sidebar - Outras notícias (visível apenas no desktop) */}
        <div className="xl:col-span-4 order-2 hidden xl:block mt-0">
          <div className="px-4 sm:px-6   xl:mt-0">
            <div className="space-y-4 sm:space-y-6">
              {outrasNoticias.length > 0 ? (
                outrasNoticias.map((noticia: any) => {
                  const link = `/noticias-eventos/noticias/${noticia.slug}`
                  const image = (noticia?.image?.url) || (noticia?.Image?.url) || noticia.imgUrl
                  const title = noticia.title || noticia.titulo
                  const date = formatarData(noticia.createdAt || noticia.dataPublicacao)

                  return (
                    <CardNewsMedium
                      key={noticia.documentId}
                      image={image}
                      title={title}
                      date={date}
                      link={link}
                      onClick={() => (window.location.href = link)}
                    />
                  )
                })
              ) : (
                <p className="text-gray-500 text-center py-8">Não há outras notícias disponíveis no momento.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Galeria de fotos - Carousel */}
      {galeriaImages.length > 0 && (
        <div className="mt-12 sm:mt-16 lg:mt-20">
          <div className="flex items-center justify-between mb-6 sm:mb-8 lg:mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-left font-exo-2">
              {titleGaleria || 'Galeria de Fotos'}
            </h2>
            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {galeriaImages.length} {galeriaImages.length === 1 ? 'foto' : 'fotos'}
            </span>
          </div>

          <Carousel className="w-full">
            <CarouselContent className="gap-4">
              {galeriaImages.map((image, index) => (
                <CarouselItem
                  key={index}
                  className={
                    index === 0
                      ? 'basis-auto shrink-0'
                      : 'basis-auto shrink-0'
                  }
                >
                  <button
                    type="button"
                    onClick={() => openGalleryModal(index)}
                    className={
                      index % 2 === 0
                        ? 'group block w-[85vw] sm:w-[75vw] md:w-[65vw] lg:w-[460px] h-56 sm:h-64 md:h-72 lg:h-80 rounded-xl overflow-hidden bg-gray-50 hover:shadow-lg transition'
                        : 'group block w-[65vw] sm:w-[55vw] md:w-[45vw] lg:w-56 h-40 sm:h-48 md:h-56 lg:h-80 rounded-xl overflow-hidden bg-gray-50 hover:shadow-lg transition'
                    }
                    aria-label={`Ver imagem ${index + 1} de ${galeriaImages.length}: ${image.alt}`}
                    title={`Clique para ampliar - ${image.alt}`}
                  >
                    <div className="relative w-full h-full">
                      {imageLoadingStates[index] && (
                        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
                      )}
                      <Image
                        src={image.url}
                        alt={image.alt}
                        fill
                        className="object-cover"
                        onLoadingComplete={() => handleImageLoad(index)}
                      />
                    </div>
                  </button>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="-left-3 top-1/2 -translate-y-1/2" aria-label="Anterior" />
            <CarouselNext className="-right-3 top-1/2 -translate-y-1/2" aria-label="Seguinte" />
          </Carousel>
        </div>
      )}

      {/* Outras notícias - Mobile e Tablet (abaixo da galeria) */}
      <div className="xl:hidden mt-12 sm:mt-16 lg:mt-20">
        <div className="rounded-lg p-4 sm:p-6 lg:p-8">
          {outrasNoticias.length > 0 ? (
            <>
              {/* Mobile: Carousel */}
              <div className="md:hidden">
                <Carousel className="w-full">
                  <CarouselContent>
                    {outrasNoticias.map((noticia: any) => {
                      const link = `/noticias-eventos/noticias/${noticia.slug}`
                      const image = (noticia?.image?.url) || (noticia?.Image?.url) || noticia.imgUrl
                      const title = noticia.title || noticia.titulo
                      const date = formatarData(noticia.createdAt || noticia.dataPublicacao)

                      return (
                        <CarouselItem key={noticia.documentId}>
                          <div className="px-2">
                            <CardNewsMedium
                              image={image}
                              title={title}
                              date={date}
                              link={link}
                              onClick={() => (window.location.href = link)}
                            />
                          </div>
                        </CarouselItem>
                      )
                    })}
                  </CarouselContent>
                </Carousel>
              </div>

              {/* Tablet: List */}
              <div className="hidden md:block space-y-4 sm:space-y-6">
                {outrasNoticias.map((noticia: any) => {
                  const link = `/noticias-eventos/noticias/${noticia.slug}`
                  const image = (noticia?.image?.url) || (noticia?.Image?.url) || noticia.imgUrl
                  const title = noticia.title || noticia.titulo
                  const date = formatarData(noticia.createdAt || noticia.dataPublicacao)

                  return (
                    <CardNewsMedium
                      key={noticia.documentId}
                      image={image}
                      title={title}
                      date={date}
                      link={link}
                      onClick={() => (window.location.href = link)}
                    />
                  )
                })}
              </div>
            </>
          ) : (
            <p className="text-gray-500 text-center py-8">Não há outras notícias disponíveis no momento.</p>
          )}
        </div>
      </div>

      {/* Modal da galeria com melhorias */}
      <Modal
        isOpen={isGalleryModalOpen}
        onClose={closeGalleryModal}
        title={`Galeria de Fotos (${selectedImageIndex + 1}/${galeriaImages.length})`}
        maxWidth="6xl"
      >
        <div className="p-4">
          <Carousel
            className="w-full"
            opts={{
              startIndex: selectedImageIndex,
              align: "center",
              loop: galeriaImages.length > 1
            }}
          >
            <CarouselContent>
              {galeriaImages.map((image, index) => (
                <CarouselItem key={index}>
                  <div className="relative aspect-video w-full max-h-[70vh] bg-gray-100 rounded-lg overflow-hidden">
                    <Image
                      src={image.url}
                      alt={image.alt}
                      fill
                      className="object-contain"
                      sizes="90vw"
                      quality={95}
                    />
                  </div>
                  <div className="text-center mt-4 space-y-2">
                    <p className="text-gray-600">
                      {index + 1} de {galeriaImages.length}
                    </p>
                    <p className="text-sm text-gray-500 max-w-md mx-auto">
                      {image.alt}
                    </p>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="absolute left-4 top-1/2 bg-white/90 hover:bg-white shadow-lg" />
            <CarouselNext className="absolute right-4 top-1/2 bg-white/90 hover:bg-white shadow-lg" />
          </Carousel>
        </div>
      </Modal>

      {/* Navegação entre notícias */}
      <div className="mt-6 sm:mt-46 lg:mt-20 flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6">
        {/* Botões de navegação */}
        <div className="flex justify-center items-center flex-1">
          {outrasNoticias && outrasNoticias.length > 0 && (
            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (outrasNoticias.length > 0) {
                    window.location.href = `/noticias-eventos/noticias/${outrasNoticias[0].slug}`;
                  }
                }}
                disabled={outrasNoticias.length === 0}
                className={`px-6 py-2 rounded-full cursor-pointer border ${outrasNoticias.length > 0 ? 'text-gray-500 border-gray-500 hover:text-greeniefp bg-white' : 'text-gray-300 border-gray-300 bg-white cursor-not-allowed'}`}
                aria-label="Notícia anterior"
              >
                Voltar
              </button>
              <button
                onClick={() => {
                  if (outrasNoticias.length > 1) {
                    window.location.href = `/noticias-eventos/noticias/${outrasNoticias[outrasNoticias.length - 1].slug}`;
                  }
                }}
                disabled={outrasNoticias.length <= 1}
                className={`px-6 py-2 rounded-full cursor-pointer border ${outrasNoticias.length > 1 ? 'text-gray-500 border-gray-500 hover:text-greeniefp bg-white' : 'text-gray-300 border-gray-300 bg-white cursor-not-allowed'}`}
                aria-label="Próxima notícia"
              >
                Próximo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoticiaDetalheClient;
